import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Icon, { type IconName } from "../AppIcon";
import { useToast, ToastRenderer } from "../../hooks/useToast";
import {
  useSubscriptionBannerState,
  type BannerState,
  type BannerVariant,
} from "../../hooks/useSubscriptionBannerState";
import { useBilling } from "../../hooks/useBilling";
import { cn } from "../../utils/cn";
import BrochureOverlay from "./BrochureOverlay";
import { requestUpgrade, notifyHrToUpgrade } from "../../api/General/Subscription";
import { Roles } from "../../models/general";
import { track, AnalyticsEvents } from "../../utils/analytics";

/**
 * Global sticky banner shown to Free / Trial / soon-to-expire-Paid users.
 *
 * Lives in MasterLayout immediately below the TopBar so every routed page
 * inherits it. Operators don't see it; users on healthy long-term Paid plans
 * don't see it. The whole band is a single big "expand" affordance — that's
 * intentional: a free user shouldn't have to hunt for the chevron.
 *
 * Design rationale (impeccable: distill + harden):
 *   - Slim, ~44px, single line on desktop. We don't want to take a giant chunk
 *     of vertical estate from people who paid us by reading a banner about
 *     paying us.
 *   - Tone is set by tier+status, not loudness. The Free band is calm indigo;
 *     a healthy Trial is calm amber; a Trial in its final week is amber-100
 *     with a stronger border to nudge — but not flash.
 *   - Single primary CTA. The "see what's included" affordance is the whole
 *     band, not a competing button. Two CTAs in a banner is a recipe for
 *     ignoring both.
 *   - Operator-preview hook: `?previewTier=free|trial|paid` lets an Operator
 *     dogfood the banner without changing any company state.
 */

/*
  The variant state machine now lives in `hooks/useSubscriptionBannerState` — the
  brochure route has to admit on exactly the condition this band renders on, and
  two copies of a five-variant machine drift. Re-exported here so existing
  importers (BrochureOverlay) keep their import path.
*/
export type { BannerVariant, BannerState };

const SubscriptionBanner: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { state } = useSubscriptionBannerState();
  const role = useSelector((s: { user?: { role?: string } }) => s.user?.role);
  const { toast, showToast, dismissToast } = useToast();

  /**
   * Billing is HR/Admin's job. A Manager or an Employee can neither pay an invoice nor renew a
   * term, so every CTA that leads to a billing surface is routed to a nudge instead of a lie.
   * The `free` variant already did this; the paid/trial variants did not, which is how a Manager
   * ended up looking at "Request renewal".
   */
  const canManageBilling =
    role === Roles.Hr || role === "Hr" || role === Roles.Admin || role === "Admin";

  /*
   * PRECEDENCE. When the tenant has already dropped to Restricted/Dormant, `RestrictedBanner` is
   * the true and more severe statement ("your account is read-only") and it carries the only CTA
   * that resolves it. Rendering this band above it produced two stacked bands with contradictory
   * calls to action on one page. The most severe true state wins; this one stands down.
   * Shared module-level store, so this costs no extra request.
   */
  const { isRestricted } = useBilling(canManageBilling);

  const [expanded, setExpanded] = useState(false);
  const [requestPending, setRequestPending] = useState(false);
  const isRtl = i18n.dir() === "rtl";


  // -------------------- Body scroll-lock while overlay open --------------------
  useEffect(() => {
    if (!expanded) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [expanded]);

  const handleToggle = useCallback(() => setExpanded((v) => !v), []);
  const handleClose = useCallback(() => setExpanded(false), []);

  /*
    CO-227 — WAS `handleStartTrial`, NAVIGATING TO `/start-trial`.

    Trial is retired, so the Free-tier CTA now leads to the one place a Free tenant can actually
    change their tier: the billing page and its plan picker. The role precondition is unchanged and
    still a precondition, not an alternative — /settings/billing is Hr/Admin-only, so a Manager who
    clicked through would have landed on access-denied.
  */
  const handleSeePlans = useCallback(
    (e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      track(AnalyticsEvents.SeePlansCtaClicked, { source: "banner", variant: state?.variant });
      if (canManageBilling) {
        setExpanded(false);
        navigate("/settings/billing");
      } else {
        showToast(t("Please ask your HR or admin to upgrade."), "info");
      }
    },
    [canManageBilling, navigate, showToast, state, t]
  );

  const handleRequestUpgrade = useCallback(
    async (e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      if (requestPending) return;
      setRequestPending(true);
      try {
        const res = await requestUpgrade({ message: null });
        if (res.errors && res.errors.length > 0) {
          showToast(
            res.errors[0]?.description ||
              t("We couldn't send your request — please try again."),
            "error"
          );
        } else {
          track(AnalyticsEvents.UpgradeRequestSubmitted, { source: "banner" });
          showToast(t("Request sent — we'll be in touch."), "success");
        }
      } catch {
        showToast(t("We couldn't send your request — please try again."), "error");
      } finally {
        setRequestPending(false);
      }
    },
    [requestPending, showToast, t]
  );

  const handleNudgeHr = useCallback(
    async (e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      if (requestPending) return;
      setRequestPending(true);
      try {
        const res = await notifyHrToUpgrade();
        if (res.errors && res.errors.length > 0) {
          showToast(
            res.errors[0]?.description ||
              t("Couldn't notify your HR — please contact support."),
            "error"
          );
        } else {
          track(AnalyticsEvents.NudgeHrSubmitted, { source: "banner" });
          showToast(t("Your HR has been notified."), "success");
        }
      } catch {
        showToast(t("Couldn't notify your HR — please contact support."), "error");
      } finally {
        setRequestPending(false);
      }
    },
    [requestPending, showToast, t]
  );

  // -------------------- Render guards --------------------
  if (!state) return null;
  if (isRestricted) return null;

  const palette = paletteFor(state.variant);
  const message = messageFor(state, t);

  // Per-variant primary CTA. Closes over the same handlers so the band stays
  // the canonical "expand" target while the button performs the verb.
  let primary: { label: string; onClick: (e: React.MouseEvent) => void } | null = null;
  switch (state.variant) {
    case "free":
      primary = canManageBilling
        ? { label: t("See plans"), onClick: handleSeePlans }
        : { label: t("Ask your HR to upgrade"), onClick: handleNudgeHr };
      break;
    case "trial-healthy":
      // No billing surface behind it, so every role gets the same honest affordance.
      primary = { label: t("See what's included"), onClick: handleToggle };
      break;
    case "trial-ending":
      primary = canManageBilling
        ? { label: t("Request upgrade"), onClick: handleRequestUpgrade }
        : { label: t("Ask your HR to upgrade"), onClick: handleNudgeHr };
      break;
    /*
      CO-227 — REACHABLE, JUST NO LONGER PRODUCIBLE. Nothing sets Free + TrialExpired any more (and
      the CO-227 migration writes Active, not TrialExpired, onto the tenants it converts), but rows
      that already carry the status keep rendering this band until they are normalised. It now
      points at the plans instead of offering a second trial.
    */
    case "trial-expired":
      primary = canManageBilling
        ? { label: t("See plans"), onClick: handleSeePlans }
        : { label: t("Ask your HR to upgrade"), onClick: handleNudgeHr };
      break;
    case "paid-expiring":
      primary = canManageBilling
        ? { label: t("Request renewal"), onClick: handleRequestUpgrade }
        : { label: t("Ask your HR to renew"), onClick: handleNudgeHr };
      break;
  }

  return (
    <>
      {/*
        Sticky band. We deliberately use a button-like div so the whole row is
        a single tap target without breaking the visual rhythm with a <button>
        outline. The aria-expanded + role="button" make it accessible regardless.
      */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls="subscription-brochure-overlay"
        aria-label={t("Subscription status — click to expand details")}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
        className={cn(
          // Sticky directly under the 64px TopBar. The z-index sits below the
          // PendingTermsGate (which is z-[100]+) and above page content.
          "sticky top-0 z-30 w-full",
          "border-b transition-colors duration-200 cursor-pointer select-none",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
          palette.band
        )}
      >
        <div className="mx-auto flex h-11 max-w-7xl items-center gap-3 px-4 md:px-6 rtl:flex-row-reverse">
          {/* Status pip — a tiny dot, not a badge, so the band stays slim */}
          <span
            aria-hidden="true"
            className={cn("inline-block h-2 w-2 flex-shrink-0 rounded-full", palette.pip)}
          />

          <Icon
            name={palette.icon}
            size={16}
            className={cn("flex-shrink-0", palette.iconClass)}
            aria-hidden="true"
          />

          {/* Message — single line, truncates on small screens so the CTA never disappears */}
          <p className={cn("flex-1 truncate text-sm font-medium", palette.text)}>
            {message}
          </p>

          {/* Primary CTA — kept as a real button so its keyboard semantics don't
              collide with the band-as-button. stopPropagation prevents the
              outer click from also toggling the overlay. */}
          {primary && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                primary!.onClick(e);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className={cn(
                "hidden md:inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold",
                "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
                palette.cta
              )}
            >
              {primary.label}
              <Icon name="ArrowRight" size={12} className={isRtl ? "rotate-180" : ""} />
            </button>
          )}

          {/* Expand chevron — flips on toggle. aria-label keeps it speakable. */}
          <span
            aria-hidden="true"
            className={cn(
              "flex-shrink-0 transition-transform duration-200",
              expanded ? "rotate-180" : "rotate-0",
              palette.iconClass
            )}
          >
            <Icon name="ChevronDown" size={16} />
          </span>
        </div>
      </div>

      {/*
        Brochure overlay — anchored below the 64px TopBar + this 44px banner.
        Lives in a separate component so the banner stays focused on the slim
        sticky surface and the overlay can own focus-trap / ESC / scroll-lock.
      */}
      <BrochureOverlay
        open={expanded}
        onClose={handleClose}
        state={state}
        /*
          The overlay repeats the band's CTAs at full size, so it needs the same role fact the band
          uses to choose them. Without it the overlay offered "See plans" and "Request upgrade" to a
          Manager — one route the Manager is refused, one endpoint that 403s.
        */
        canManageBilling={canManageBilling}
        onSeePlans={handleSeePlans}
        onRequestUpgrade={handleRequestUpgrade}
        onNudgeHr={handleNudgeHr}
      />

      {/* Local toaster — used by the "Request sent" stub and the "ask your admin" fallback. */}
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Variant -> message / CTA helpers (kept beside the component so the state
// machine is readable end-to-end without jumping files)
// ─────────────────────────────────────────────────────────────────────────────

function messageFor(
  state: BannerState,
  t: (k: string, o?: Record<string, unknown>) => string
): string {
  switch (state.variant) {
    case "free":
      return t("You're on the free version of Mowazaf — explore everything you get.");
    case "trial-healthy":
      return t("{{days}} days left in your trial.", {
        days: Math.max(0, state.daysRemaining ?? 0),
      });
    case "trial-ending": {
      // Same rule as renewal: never render a negative or a "-1 days" count.
      const days = state.daysRemaining ?? 0;
      if (days <= 0) return t("Your trial has ended — request continuation.");
      return t("Trial ends in {{days}} days — request continuation.", { days });
    }
    case "trial-expired":
      return t("Trial ended — upgrade to keep your full workspace.");
    case "paid-expiring": {
      /*
        A day count is only ever a count. Once the date is behind us the honest sentence is that
        the renewal is late, not that it is due in a negative number of days — which is what this
        band showed on every lapsed tenant.
      */
      const days = state.daysRemaining ?? 0;
      if (days <= 0) return t("Renewal is overdue.");
      if (days === 1) return t("Renewal due tomorrow.");
      return t("Renewal due in {{days}} days.", { days });
    }
    default:
      return "";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Visual palette per variant
// ─────────────────────────────────────────────────────────────────────────────

interface BannerPalette {
  band: string;
  text: string;
  icon: IconName;
  iconClass: string;
  pip: string;
  cta: string;
}

function paletteFor(variant: BannerVariant): BannerPalette {
  switch (variant) {
    case "free":
      return {
        band: "bg-indigo-50 border-indigo-100 hover:bg-indigo-100/70",
        text: "text-indigo-900",
        icon: "Sparkles",
        iconClass: "text-indigo-600",
        pip: "bg-indigo-500",
        cta:
          "bg-indigo-600 text-white hover:bg-indigo-700 focus-visible:ring-indigo-500",
      };
    case "trial-healthy":
      return {
        band: "bg-amber-50 border-amber-100 hover:bg-amber-100/70",
        text: "text-amber-900",
        icon: "Rocket",
        iconClass: "text-amber-700",
        pip: "bg-amber-500",
        cta:
          "bg-amber-600 text-white hover:bg-amber-700 focus-visible:ring-amber-500",
      };
    case "trial-ending":
      return {
        band: "bg-amber-100 border-amber-300 hover:bg-amber-200/70",
        text: "text-amber-950",
        icon: "Clock",
        iconClass: "text-amber-800",
        pip: "bg-amber-600",
        cta:
          "bg-amber-700 text-white hover:bg-amber-800 focus-visible:ring-amber-600",
      };
    case "trial-expired":
      return {
        band: "bg-rose-50 border-rose-200 hover:bg-rose-100/70",
        text: "text-rose-900",
        icon: "AlertCircle",
        iconClass: "text-rose-600",
        pip: "bg-rose-500",
        cta: "bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-500",
      };
    case "paid-expiring":
      return {
        band: "bg-slate-50 border-slate-200 hover:bg-slate-100/70",
        text: "text-slate-900",
        icon: "CalendarClock",
        iconClass: "text-slate-600",
        pip: "bg-slate-500",
        cta:
          "bg-slate-900 text-white hover:bg-slate-800 focus-visible:ring-slate-700",
      };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Re-export the primary-CTA factory so JSX above can pick the right action.
// (We don't actually export it — but the inline version in the component uses
//  the same switch. Keeping the canonical source here for future test coverage.)
// ─────────────────────────────────────────────────────────────────────────────

export default SubscriptionBanner;
